import React, { useState } from "react";
import ReactDOM from "react-dom";
import { ICellRendererParams } from "ag-grid-community";
import { IconWithTooltip } from "@/components/Icons/IconFactory";
import Dialog from "@/components/Dialog/Dialog";

interface ActionCellRendererProps extends ICellRendererParams {
  data: {
    id: string;
    commodity: string;
    isDefaultValue: boolean;
  };
}

export const ActionCellRenderer: React.FC<ActionCellRendererProps> = (params) => {
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCommodity, setSelectedCommodity] = useState("");

  const handleResetClick = () => {
    setSelectedCommodity(params.data?.commodity);
    setIsResetDialogOpen(true);
  };

  const handleEditClick = () => {
    setSelectedCommodity(params.data?.commodity === "Default threshold" ? "Default" : params.data?.commodity);
    setIsEditDialogOpen(true);
  };

  const handleResetDialogClose = () => {
    setIsResetDialogOpen(false);
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
  };

  const handleConfirmReset = async () => {
    try {
      const response = await fetch(`/api/risk-threshold/${params.data?.id}/reset`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        console.log("Reset confirmed for", selectedCommodity);
        params.api?.refreshCells({ force: true });
      }
    } catch (error) {
      console.error("Failed to reset threshold:", error);
    } finally {
      setIsResetDialogOpen(false);
    }
  };

  const handleConfirmEdit = async () => {
    try {
      const response = await fetch(`/api/risk-threshold/${params.data?.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // Add your threshold data here
        }),
      });

      if (response.ok) {
        console.log("Edit saved for", selectedCommodity);
        params.api?.refreshCells({ force: true });
      }
    } catch (error) {
      console.error("Failed to save threshold:", error);
    } finally {
      setIsEditDialogOpen(false);
    }
  };

  const isPinnedRow = params.node.rowPinned === "top";

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
        <IconWithTooltip
          iconName="edit"
          onClick={handleEditClick}
          style={{ cursor: 'pointer' }}
        />
        {!isPinnedRow && (
          <IconWithTooltip
            iconName="reset"
            size={20}
            isDefaultValue={params.data?.isDefaultValue}
            onClick={handleResetClick}
            style={{ cursor: 'pointer' }}
          />
        )}
      </div>
      {isResetDialogOpen && ReactDOM.createPortal(
        <Dialog
          title={`Are you sure you want to reset ${selectedCommodity} to the Default Thresholds?`}
          center={true}
          isActionButtonVisible={true}
          actionButtonTitle="Yes"
          isCancelButtonVisible={true}
          cancelButtonTitle="Cancel"
          onClose={handleResetDialogClose}
          onAction={handleConfirmReset}
        >
          <div className="mt-4">Resetting will overwrite the current thresholds.</div>
        </Dialog>,
        document.body
      )}
      {isEditDialogOpen && ReactDOM.createPortal(
        <Dialog
          title={`Edit ${selectedCommodity} Thresholds`}
          center={true}
          isActionButtonVisible={true}
          actionButtonTitle="Save"
          isCancelButtonVisible={true}
          cancelButtonTitle="Cancel"
          onClose={handleEditDialogClose}
          onAction={handleConfirmEdit}
        >
          <div className="mt-4">{/* Add your edit form fields here */}</div>
        </Dialog>,
        document.body
      )}
    </>
  );
};